import * as R from 'ramda'
import React, { useCallback, useEffect, useState } from 'react'
import type { ColorWrapChangeHandler } from 'react-color/lib/components/common/ColorWrap'

import Input, { AdditionWrapper } from 'components/uiKit/Input'
import InputNumber from 'components/uiKit/InputNumber'
import { KitSize } from 'components/uiKit/KitTypes'

import { validateHEX, hex2rgb } from '../../helpers'
import * as s from './HexFields.module.scss'

interface IUpperFloorProps {
  hex?: string
  onChange: ColorWrapChangeHandler
  alpha?: number
}

const getStringHEX = (hex?: string) => hex?.toUpperCase()?.slice(1, 7)

const HexFields = ({
  hex: hexValue,
  alpha: alphaValue,
  onChange,
}: IUpperFloorProps): JSX.Element => {
  const [hex, setHex] = useState(getStringHEX(hexValue))
  const [alpha, setAlpha] = useState<number | null>(Math.round((alphaValue ?? 1) * 100))

  const onChangeHEX = useCallback(
    (value: string) => {
      const newValue = validateHEX(value.toUpperCase()) || ''
      setHex(newValue)

      if (newValue.length === 6) {
        onChange(hex2rgb(`#${newValue}`, R.clamp(0, 100, alpha || 0) / 100))
      }
    },
    [alpha, onChange],
  )

  const onChangeAlpha = useCallback(
    (value: number | null) => {
      setAlpha(value)

      if (hex?.length === 6 && R.isNotNil(value) && value >= 0 && value <= 100) {
        onChange(hex2rgb(`#${hex}`, R.clamp(0, 100, value || 0) / 100))
      }
    },
    [hex, onChange],
  )

  const handleBlur = useCallback(
    () => setAlpha(R.clamp(0, 100, Math.round((alphaValue ?? 1) * 100))),
    [alphaValue],
  )

  useEffect(() => {
    setHex(getStringHEX(hexValue))
    setAlpha(Math.round((alphaValue ?? 1) * 100))
  }, [alphaValue, hexValue])

  return (
    <div className={s.root}>
      <div className={s.label}>Hex</div>
      <div className={s.inputHex}>
        <Input
          name='hex'
          onChange={onChangeHEX}
          prefix={<AdditionWrapper>#</AdditionWrapper>}
          size={KitSize.S}
          value={hex}
        />
      </div>
      <div className={s.inputAlpha}>
        <InputNumber
          error={alpha === null || alpha < 0 || alpha > 100}
          name='alpha'
          onBlur={handleBlur}
          onChange={onChangeAlpha}
          postfix={<AdditionWrapper>%</AdditionWrapper>}
          size={KitSize.S}
          value={alpha}
        />
      </div>
    </div>
  )
}

export default HexFields
